import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, MessageSquare, CheckCircle, Download } from "lucide-react";

interface FunnelStep {
  label: string;
  count: number;
  percentage: number;
  icon: React.ReactNode;
  isQuestion?: boolean;
}

interface FunnelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  formTitle: string;
}

export function FunnelDialog({ open, onOpenChange, formId, formTitle }: FunnelDialogProps) {
  const [loading, setLoading] = useState(true);
  const [funnelSteps, setFunnelSteps] = useState<FunnelStep[]>([]);

  useEffect(() => {
    if (open && formId) {
      fetchFunnelData();
    }
  }, [open, formId]);

  const fetchFunnelData = async () => {
    setLoading(true);
    try {
      // 1. Get form details (fields count and ai_question_count)
      const { data: form } = await supabase
        .from("forms")
        .select("ai_question_count")
        .eq("id", formId)
        .single();

      const { data: formFields } = await supabase
        .from("form_fields")
        .select("id")
        .eq("form_id", formId);

      const fixedFieldsCount = formFields?.length || 0;
      const aiQuestionCount = form?.ai_question_count || 5;

      // 2. Get total sessions (leads that started the form)
      const { count: totalSessions } = await supabase
        .from("form_sessions")
        .select("*", { count: "exact", head: true })
        .eq("form_id", formId);

      const total = totalSessions || 0;

      if (total === 0) {
        setFunnelSteps([
          {
            label: "Iniciaram o formulário",
            count: 0,
            percentage: 0,
            icon: <Users className="h-4 w-4" />,
          },
        ]);
        setLoading(false);
        return;
      }

      // 3. Get all sessions for this form to analyze responses
      const { data: sessions } = await supabase
        .from("form_sessions")
        .select("id, status, pdf_url")
        .eq("form_id", formId);

      if (!sessions || sessions.length === 0) {
        setFunnelSteps([
          {
            label: "Iniciaram o formulário",
            count: 0,
            percentage: 0,
            icon: <Users className="h-4 w-4" />,
          },
        ]);
        setLoading(false);
        return;
      }

      const sessionIds = sessions.map((s) => s.id);

      // 4. Get all responses (excluding special __PDF_DOWNLOAD__ entries for question display)
      const { data: responses } = await supabase
        .from("form_responses")
        .select("session_id, question_order, question, is_ai_generated")
        .in("session_id", sessionIds)
        .order("question_order");

      // Separate PDF download responses from regular responses
      const regularResponses = responses?.filter(r => r.question !== "__PDF_DOWNLOAD__") || [];
      const pdfDownloadResponses = responses?.filter(r => r.question === "__PDF_DOWNLOAD__") || [];
      
      // Count unique sessions that downloaded PDF
      const pdfDownloadSessions = new Set(pdfDownloadResponses.map(r => r.session_id));

      // 5. Calculate funnel steps
      const steps: FunnelStep[] = [];

      // Step 1: Started form
      steps.push({
        label: "Iniciaram o formulário",
        count: total,
        percentage: 100,
        icon: <Users className="h-4 w-4" />,
      });

      // Separate fixed fields from AI questions
      // Fixed fields have question_order <= fixedFieldsCount
      // AI questions have question_order > fixedFieldsCount
      
      // Group fixed fields by question_order
      const fixedFieldGroups = new Map<number, { sessions: Set<string>; question: string }>();
      // Group AI questions by their relative order (1st AI question, 2nd AI question, etc.)
      const aiQuestionGroups = new Map<number, Set<string>>();
      
      regularResponses.forEach((r) => {
        if (r.question_order <= fixedFieldsCount) {
          // Fixed field
          if (!fixedFieldGroups.has(r.question_order)) {
            fixedFieldGroups.set(r.question_order, { sessions: new Set(), question: r.question });
          }
          fixedFieldGroups.get(r.question_order)!.sessions.add(r.session_id);
        } else if (r.question_order < 9999) { // Exclude special markers (9999)
          // AI question - calculate relative order (1st, 2nd, 3rd AI question)
          const aiOrder = r.question_order - fixedFieldsCount;
          // Only count up to the configured ai_question_count
          if (aiOrder <= aiQuestionCount) {
            if (!aiQuestionGroups.has(aiOrder)) {
              aiQuestionGroups.set(aiOrder, new Set());
            }
            aiQuestionGroups.get(aiOrder)!.add(r.session_id);
          }
        }
      });

      // Add fixed field steps
      const sortedFixedOrders = Array.from(fixedFieldGroups.keys()).sort((a, b) => a - b);
      sortedFixedOrders.forEach((order) => {
        const group = fixedFieldGroups.get(order)!;
        const count = group.sessions.size;
        const questionLabel = group.question.length > 35 
          ? group.question.substring(0, 35) + "..." 
          : group.question;
        
        steps.push({
          label: questionLabel,
          count,
          percentage: Math.round((count / total) * 100),
          icon: <MessageSquare className="h-4 w-4" />,
          isQuestion: true,
        });
      });

      // Add AI question steps (grouped by order, not by question text)
      const sortedAiOrders = Array.from(aiQuestionGroups.keys()).sort((a, b) => a - b);
      sortedAiOrders.forEach((aiOrder) => {
        const sessionSet = aiQuestionGroups.get(aiOrder)!;
        const count = sessionSet.size;
        
        steps.push({
          label: `${aiOrder}ª Pergunta IA`,
          count,
          percentage: Math.round((count / total) * 100),
          icon: <MessageSquare className="h-4 w-4" />,
          isQuestion: true,
        });
      });

      // Completed sessions (status = 'completed')
      const completedCount = sessions.filter((s) => s.status === "completed").length;
      steps.push({
        label: "Completaram o formulário",
        count: completedCount,
        percentage: Math.round((completedCount / total) * 100),
        icon: <CheckCircle className="h-4 w-4" />,
      });

      // PDF downloads (tracked via __PDF_DOWNLOAD__ response)
      const pdfCount = pdfDownloadSessions.size;
      steps.push({
        label: "Baixaram o PDF",
        count: pdfCount,
        percentage: Math.round((pdfCount / total) * 100),
        icon: <Download className="h-4 w-4" />,
      });

      setFunnelSteps(steps);
    } catch (error) {
      console.error("Error fetching funnel data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto pb-[100px]">
        <DialogHeader>
          <DialogTitle className="font-display">
            Funil de Conversão
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{formTitle}</p>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : funnelSteps.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum dado disponível ainda.
            </p>
          ) : (
            <div className="space-y-1">
              {funnelSteps.map((step, index) => {
                // Calculate width based on percentage (minimum 15% for visibility)
                const barWidth = Math.max(step.percentage, 15);
                
                return (
                  <div key={index} className="relative h-12">
                    {/* Background bar - proportional width */}
                    <div
                      className={`absolute left-0 top-0 h-full rounded-lg transition-all ${
                        step.isQuestion 
                          ? "bg-primary/15" 
                          : index === 0 
                            ? "bg-primary/25" 
                            : "bg-accent/25"
                      }`}
                      style={{ width: `${barWidth}%` }}
                    />
                    
                    {/* Content - full width, text can overflow bar */}
                    <div className="absolute inset-0 flex items-center justify-between px-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className={`flex-shrink-0 ${
                          step.isQuestion ? "text-primary" : "text-accent"
                        }`}>
                          {step.icon}
                        </span>
                        <span className="text-sm font-medium truncate">
                          {step.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <span className="text-lg font-bold">{step.count}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          ({step.percentage}%)
                        </span>
                      </div>
                    </div>

                    {/* Connector line */}
                    {index < funnelSteps.length - 1 && (
                      <div className="absolute -bottom-1 left-6 w-0.5 h-2 bg-border z-10"></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!loading && funnelSteps.length > 0 && funnelSteps[0].count > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Taxa de conversão total:</span>
              <span className="font-bold text-accent">
                {funnelSteps[funnelSteps.length - 1]?.percentage || 0}%
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
